import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as crmApi from '../../../services/crmApi';
import * as fileApi from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { getSlimLogoPath } from '../../../utils/slimLogo';
import {
  documentsMatchPrerequisiteSlot,
  mapDocumentTypeToCategory,
  resolveRequiredDocumentsFromPrerequisites
} from '../../../utils/applicationDocumentTypes';

const formatDisplayName = (firstName, lastName, fallback = 'Unknown') =>
  [firstName, lastName].filter(Boolean).join(' ').trim() || fallback;

const PublicDocumentSubmit = ({ entityType = 'application' } = {}) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const isEnrollment = entityType === 'enrollment';
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  // Track which document fileId is assigned to which field (category + title combination)
  const [documentFieldMap, setDocumentFieldMap] = useState({});

  const handleViewOrDownloadDocument = async (doc, { download } = { download: false }) => {
    try {
      if (!doc?.fileId) {
        toast.error('Document file ID not found');
        return;
      }

      if (!application?._id) {
        toast.error(isEnrollment ? 'Enrollment not loaded' : 'Application not loaded');
        return;
      }

      const response = isEnrollment
        ? await fileApi.getEnrollmentDocumentPresignedUrlPublic(application._id, doc.fileId)
        : await fileApi.getApplicationDocumentPresignedUrlPublic(application._id, doc.fileId);
      const url = response.data?.data?.presignedUrl || response.data?.presignedUrl || response.data?.data?.url || response.data?.url;

      if (!url) {
        toast.error('Could not retrieve document URL');
        return;
      }

      if (!download) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName || doc.fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error getting document URL:', error);
      if (error.response?.status === 404 || error.response?.data?.message === 'File not found') {
        toast.error('Document not found');
      } else {
        toast.error(error.response?.data?.message || 'Failed to open document');
      }
    }
  };

  const selectedRequirementCategoryName = React.useMemo(() => {
    const selectedId = application?.requirementsCategoryId;
    const categories = application?.subProductId?.requirementCategories;
    if (!selectedId || !Array.isArray(categories) || categories.length === 0) return null;
    const match = categories.find((c) => String(c?._id) === String(selectedId));
    return match?.name || null;
  }, [application]);

  useEffect(() => {
    fetchApplicationByToken();
  }, [token]);

  const fetchApplicationByToken = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = isEnrollment
        ? await crmApi.getEnrollmentByDocumentToken(token)
        : await crmApi.getApplicationByDocumentToken(token);
      console.log(isEnrollment ? 'Enrollment response:' : 'Application response:', response.data);
      const appData = response.data.data || response.data;
      // Enrollment API already aliases requiredDocuments -> documents
      if (isEnrollment && !appData.documents) {
        appData.documents = appData.requiredDocuments || [];
      }
      setApplication(appData);
      
      // Initialize uploaded documents from existing documents with fileId
      const existingDocs = appData.documents?.filter(doc => doc.fileId) || [];
      setUploadedDocuments(
        existingDocs.map((doc) => doc.category || 'other')
      );
      
      // Initialize document field map - this will be populated when fields are rendered
      // We'll match documents to fields by title when rendering
      setDocumentFieldMap({});
    } catch (error) {
      console.error(isEnrollment ? 'Error fetching enrollment:' : 'Error fetching application:', error);
      setError(
        error.response?.data?.message
          || (isEnrollment
            ? 'Failed to load enrollment. The link may be invalid or expired.'
            : 'Failed to load application. The link may be invalid or expired.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (category, file, maxSize = 10485760, documentTitle = '', fieldIndex = null) => {
    if (!file) return;

    // Check file size with custom limit
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      toast.error(`File size must be less than ${sizeMB}MB`);
      return;
    }

    // Use the same unique key as the render so documents sharing a category
    // (e.g. two fields both with category 'academic_certificate') don't bleed
    // their loading state onto each other.
    let finalFieldIndex = fieldIndex;
    if (finalFieldIndex === null) {
      const requiredDocs = application?.subProductId?.requirements?.prerequisites
        ?.filter(prereq => prereq.isRequired || prereq.isMandatory) || [];
      finalFieldIndex = requiredDocs.findIndex(prereq =>
        prereq.documentType === category &&
        prereq.title === documentTitle
      );
    }
    const fieldKey = finalFieldIndex >= 0
      ? `${category}_${documentTitle}_${finalFieldIndex}`
      : `${category}_${documentTitle}_${Date.now()}`;

    try {
      setUploadingFiles(prev => ({ ...prev, [fieldKey]: true }));
      
      // Map documentType to FileMetadata category
      const fileCategory = mapDocumentTypeToCategory(category);
      
      const response = isEnrollment
        ? await fileApi.uploadDocumentToEnrollment(
            application._id,
            file,
            { category: fileCategory, title: documentTitle }
          )
        : await fileApi.uploadDocumentToApplication(
            application._id,
            file,
            { category: fileCategory, title: documentTitle }
          );

      // Update uploaded documents list (store documentType for tracking)
      setUploadedDocuments(prev => [...new Set([...prev, category])]);
      
      if (response.data?.data?.fileId) {
        setDocumentFieldMap(prev => ({
          ...prev,
          [fieldKey]: response.data.data.fileId
        }));
      }
      
      toast.success(`${documentTitle || 'Document'} uploaded successfully!`);
      
      // Refresh application to show new document
      await fetchApplicationByToken();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || `Failed to upload ${category}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleRemoveUploadedDocument = async (fieldKey, doc) => {
    try {
      if (!doc?.fileId) {
        toast.error('Unable to remove document (missing file id)');
        return;
      }

      if (doc?.reviewStatus === 'APPROVED') {
        toast.error('This document has been approved and cannot be removed');
        return;
      }

      await (isEnrollment
        ? crmApi.deletePublicEnrollmentDocument(token, doc.fileId)
        : crmApi.deletePublicApplicationDocument(token, doc.fileId));

      setDocumentFieldMap((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });

      toast.success('Document removed. You can upload a new one.');
      await fetchApplicationByToken();
    } catch (error) {
      console.error('Remove document error:', error);
      toast.error(error.response?.data?.message || 'Failed to remove document');
    }
  };

  // Get required documents from sub-product prerequisites, or use defaults
  const requiredDocuments = React.useMemo(() => {
    const prerequisites = application?.subProductId?.requirements?.prerequisites || [];
    const resolved = resolveRequiredDocumentsFromPrerequisites(
      application?.subProductId,
      application?.requirementsCategoryId
    );
    // If the program defines prerequisites, use only the resolved prerequisites for the selected category
    if (prerequisites.length > 0) {
      return resolved;
    }
    if (resolved.length > 0) {
      return resolved;
    }
    
    // Fallback to default documents ONLY if no prerequisites defined on program at all
    return [
      { category: 'id_proof', label: 'National ID / Passport', accept: 'image/*,.pdf', maxSize: 10485760 },
      { category: 'academic_certificate', label: 'Educational Certificate', accept: 'image/*,.pdf', maxSize: 10485760 },
      { category: 'academic_certificate', label: 'Academic Transcript', accept: 'image/*,.pdf', maxSize: 10485760 },
      { category: 'experience_letter', label: 'Resume / CV', accept: '.pdf,.doc,.docx', maxSize: 10485760 },
    ];
  }, [application]);

  const findUploadedDocumentForSlot = React.useCallback((doc, index, assignedFileIds = []) => {
    const documentTitle = doc.title || doc.label;
    const fieldKey = `${doc.category}_${documentTitle}_${index}`;
    const assignedFileId = documentFieldMap[fieldKey];

    if (assignedFileId) {
      const assignedDoc = application?.documents?.find((item) => item.fileId === assignedFileId);
      if (assignedDoc) return assignedDoc;
    }

    return (application?.documents || []).find((item) => (
      !assignedFileIds.includes(item.fileId)
      && documentsMatchPrerequisiteSlot(item, doc)
    )) || null;
  }, [application, documentFieldMap]);

  // Check if all documents are uploaded by verifying each field has an uploaded document
  // This must be before early returns to follow Rules of Hooks
  const allDocumentsUploaded = React.useMemo(() => {
    if (!application || !requiredDocuments.length) return false;

    return requiredDocuments.every((doc, index) => Boolean(findUploadedDocumentForSlot(doc, index)));
  }, [requiredDocuments, application, findUploadedDocumentForSlot]);

  if (loading) {
    return <LoadingSpinner message={isEnrollment ? 'Loading enrollment...' : 'Loading application...'} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-4 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <img src={getSlimLogoPath()} alt="Logo" className="w-20 h-20 object-contain" />
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Document Submission</h1>
              <p className="text-sm text-gray-600">
                {isEnrollment
                  ? `Enrollment #${application.enrollmentNumber || application.applicationNumber}`
                  : `Application #${application.applicationNumber}`}
              </p>
            </div>
          </div>
          
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            <p className="text-xs text-indigo-900">
              <span className="font-semibold">Student:</span> {formatDisplayName(application.personalInfo?.firstName, application.personalInfo?.lastName, application.contactInfo?.email || 'Unknown')}
            </p>
            <p className="text-xs text-indigo-900">
              <span className="font-semibold">Email:</span> {application.contactInfo?.email}
            </p>
            <p className="text-xs text-indigo-900">
              <span className="font-semibold">Program:</span> {application.subProductId?.name || 'N/A'}
            </p>
          </div>

          {selectedRequirementCategoryName && (
            <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-xs text-indigo-900">
                <span className="font-semibold">Selected Requirement Category:</span> {selectedRequirementCategoryName}
              </p>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-lg shadow-md p-3 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Upload Instructions</h2>
          <ul className="space-y-1 text-xs text-gray-600">
            <li className="flex items-start gap-1.5">
              <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Upload clear, readable copies of all required documents</span>
            </li>
            <li className="flex items-start gap-1.5">
              <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Follow the file size and format requirements for each document</span>
            </li>
            <li className="flex items-start gap-1.5">
              <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Ensure all text in documents is clearly visible</span>
            </li>
            {application?.subProductId?.requirements?.prerequisites?.length > 0 && (
              <li className="flex items-start gap-1.5">
                <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>Read any special instructions provided for specific documents</span>
              </li>
            )}
          </ul>
        </div>

        {/* Document Upload Forms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {requiredDocuments.map(({ category, label, title, accept, maxSize, description, instructions }, index) => {
            const documentTitle = title || label;
            const fieldKey = `${category}_${documentTitle}_${index}`;
            const isUploading = uploadingFiles[fieldKey];
            const assignedFileIds = Object.values(documentFieldMap);
            let uploadedDoc = findUploadedDocumentForSlot(
              { category, title: documentTitle, label: documentTitle },
              index,
              assignedFileIds
            );

            const isUploaded = !!uploadedDoc;

            const isLocked = uploadedDoc?.reviewStatus === 'APPROVED';
            const statusLabel = isLocked ? 'Approved' : (isUploaded ? 'Uploaded' : null);

            return (
              <div key={fieldKey} className="bg-white rounded-lg shadow-md p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    {(isUploaded || uploadedDoc) ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-gray-900">{title || label}</h3>
                      {description && !isUploaded && !uploadedDoc && (
                        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                      )}
                      {(isUploaded || uploadedDoc) && uploadedDoc && (
                        <div className="mt-1">
                          <p className="text-xs text-green-600">✓ Uploaded successfully</p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate" title={uploadedDoc.originalName}>
                            {uploadedDoc.originalName} • {(uploadedDoc.fileSize / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {(isUploaded || uploadedDoc) && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                      isLocked ? 'bg-emerald-100 text-emerald-800' : 'bg-green-100 text-green-700'
                    }`}>
                      {statusLabel || 'Done'}
                    </span>
                  )}
                </div>

                {instructions && !isUploaded && !uploadedDoc && (
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    <strong>Note:</strong> {instructions}
                  </div>
                )}

                {(isUploaded || uploadedDoc) && uploadedDoc && (
                  <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    {isLocked ? (
                      <p className="text-xs text-emerald-700 font-medium">
                        This document is approved and locked.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-600">
                        You can remove and upload again if needed.
                      </p>
                    )}

                    <div className="flex items-center gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => handleViewOrDownloadDocument(uploadedDoc, { download: false })}
                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewOrDownloadDocument(uploadedDoc, { download: true })}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        Download
                      </button>

                      {!isLocked && (
                        <button
                          type="button"
                          onClick={() => handleRemoveUploadedDocument(fieldKey, uploadedDoc)}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {!isUploaded && !uploadedDoc && (
                  <div className="relative">
                    <input
                      type="file"
                      id={`file-${fieldKey}`}
                      accept={accept}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleFileUpload(category, file, maxSize, documentTitle, index);
                      }}
                      disabled={isUploading}
                      className="hidden"
                    />
                    <label
                      htmlFor={`file-${fieldKey}`}
                      className={`
                        flex items-center justify-center gap-2 w-full p-3 
                        border-2 border-dashed rounded-lg cursor-pointer
                        transition-colors
                        ${isUploading 
                          ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
                          : 'border-indigo-300 hover:border-indigo-500 bg-indigo-50 hover:bg-indigo-100'
                        }
                      `}
                    >
                      {isUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          <span className="text-xs text-gray-600 font-medium">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="w-5 h-5 text-indigo-600" />
                          <span className="text-xs text-indigo-600 font-medium">
                            Click to upload ({(maxSize / (1024 * 1024)).toFixed(1)}MB max)
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Success Message */}
        {allDocumentsUploaded && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-green-900">All Documents Uploaded!</h3>
                <p className="text-xs text-green-700 mt-0.5">
                  Thank you for submitting your documents. Our admissions team will review them shortly.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicDocumentSubmit;
